import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { IonContent } from '@ionic/angular';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Bridges Ionic's ion-content with Angular CDK's ScrollDispatcher.
 *
 * The CDK overlay's RepositionScrollStrategy uses ScrollDispatcher to detect scroll events,
 * but ScrollDispatcher only monitors window scroll and elements with cdkScrollable directive.
 * Since ion-content uses Shadow DOM, the actual scroll element is inside the shadow root,
 * not the host element, so standard cdkScrollable won't work.
 *
 * This directive gets the actual scroll element via ionContent.getScrollElement()
 * and manually registers it with ScrollDispatcher.
 */
@Directive({
  selector: 'ion-content[tidyCdkScrollable]',
  standalone: true
})
export class IonicCdkScrollableDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private scrollElement: HTMLElement | null = null;
  private scrollableRef: any = null;

  constructor(
    private ionContent: IonContent,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone,
    private elementRef: ElementRef
  ) {}

  async ngOnInit() {
    this.scrollElement = await this.ionContent.getScrollElement();

    if (this.scrollElement) {
      this.scrollableRef = {
        getElementRef: () => ({ nativeElement: this.scrollElement }),
        elementScrolled: () => fromEvent(this.scrollElement!, 'scroll'),
        ngZone: this.ngZone,
        scrollTo: () => { /* Not needed for scroll detection */ }
      };

      this.scrollDispatcher.register(this.scrollableRef);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.scrollableRef) {
      this.scrollDispatcher.deregister(this.scrollableRef);
    }
  }
}
